// @ts-strict-ignore
import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UserRightDefVO } from '@article/model/vo/user-right-def.vo';
import { UserRightsInfoVO } from '@article/model/vo/user-rights-info.vo';
import { WagoApiClient } from '@shared/service/wago-api.client';
import { Observable, of, tap } from 'rxjs';
import { map } from 'rxjs/operators';
import { ModelConversionService } from '@shared/convert/model-conversion.service';
import { LoginInfo } from '../model/login-info';
import { MdmGroupVO, UserProfileVO, UserVO } from '../model/vo/user.vo';
import { DeactivateProjectChangeActionService } from '@shared/service/deactivate-project-change-action.service';

declare function unescape(s: string): string;

@Injectable({ providedIn: 'root' })
export class UserDataService {


  constructor(
    private wagoApiClient: WagoApiClient,
    private modelConversionService: ModelConversionService,
    private deactivateProjectChangeActionService: DeactivateProjectChangeActionService,
  ) {}

  private createRequestOptions(authorizationToken: string = null): any {
    let httpHeaders = new HttpHeaders();
    httpHeaders = httpHeaders.append('Content-Type', 'application/x-www-form-urlencoded');
    if (authorizationToken) {
      httpHeaders = httpHeaders.append('WagoAuthorization', authorizationToken);
    }
    const requestOptions = {
      headers: httpHeaders,
    };
    return requestOptions;
  }

  private generateToken(loginInfo: LoginInfo): string {
    if (!loginInfo) {
      return null;
    }
    let content: string;
    if (loginInfo.type === LoginInfo.TYPE_CDC) {
      content = `${loginInfo.cdcUID}:cdc#${loginInfo.cdcUID}#${loginInfo.cdcSignature}#${loginInfo.cdcTimestamp}`;
    } else {
      content = `${loginInfo.username}:${loginInfo.password}`;
    }
    const encoded: string = encodeURIComponent(content);
    const unescaped: string = unescape(encoded);
    const base64: string = window.btoa(unescaped);
    return 'Basic ' + base64;
  }

  public login(loginInfo: LoginInfo): Observable<UserProfileVO> {
    return this.executeLogin(loginInfo, 'user/login');
  }

  private executeLogin(loginInfo: LoginInfo, loginUrl: string): Observable<UserProfileVO> {
    const token: string = this.generateToken(loginInfo);
    const requestOptions: any = this.createRequestOptions(token);
    return this.wagoApiClient
      .post(loginUrl, undefined, requestOptions, {
        isErrorHandlingRequired: false,
        bypassActiveCheckAndSessionValidation: true,
      })
      .pipe(
        map((userProfile: any) => {
          return this.modelConversionService.convertUserProfileVO(userProfile);
        }),
        tap((userProfile: UserProfileVO) => this.deactivateProjectChangeIfNeeded(userProfile))
      );
  }

  private deactivateProjectChangeIfNeeded(userProfile: UserProfileVO): void {
    // new login, clear all previous reasons.
    this.deactivateProjectChangeActionService.removeAllReasonsToDeactivateProjectChangeAction();

    if (userProfile && userProfile.user.isProduction()) {
      this.deactivateProjectChangeActionService.addReasonToDeactivateProjectChangeAction('productionUser');
    }
  }

  private canUserSaveProfile(userProfile) {
    return !(userProfile === null ||
      userProfile === undefined ||
      !userProfile.isRegisteredUserAccount() ||
      userProfile.user === null ||
      !userProfile.user.hasPermission("SAVE_USER_PROFILES"));
  }

  public saveUserSettingProperties(userProfile: UserProfileVO): Observable<any> {
    const userSettingProperties = userProfile.userSettingProperties;
    if (userSettingProperties == null || !this.canUserSaveProfile(userProfile)) {
      return of(null);
    }
    return this.wagoApiClient.post('user/usersettingproperties', userSettingProperties);
  }

  public saveReportGenerationProperties(userProfile: UserProfileVO): Observable<any> {
    const reportGenerationProperties = userProfile.reportGenerationProperties;
    if (reportGenerationProperties == null || !this.canUserSaveProfile(userProfile)) {
      return of(null);
    }

    return this.wagoApiClient.post('user/reportgenerationproperties', reportGenerationProperties);
  }

  public saveArticleCollections(userProfile: UserProfileVO): Observable<any> {
    const articleCollections = userProfile.articleCollections;
    if (articleCollections == null || !this.canUserSaveProfile(userProfile)) {
      return of(null);
    }
    return this.wagoApiClient.post('user/articlecollections', articleCollections);
  }

  public getCurrentConfiguratorUser(): Observable<UserProfileVO> {
    return this.wagoApiClient
      .post('user/current-user', null, null, {
        isErrorHandlingRequired: false,
        bypassActiveCheckAndSessionValidation: true,
      })
      .pipe(map((response) => this.modelConversionService.convertUserProfileVO(response)));
  }

  public userExists(userName: string): Observable<boolean> {
    return this.wagoApiClient.get(`user/exists/?userName=${userName}`);
  }


  public getVisibleUserRightDefs(): Observable<UserRightDefVO[]> {
    return this.wagoApiClient.get('user/rights/visible').pipe(
      map((response) => this.modelConversionService.convertUserRightDefVOs(response)));
  }

  public findUserRightsInfoByUsername(username: string): Observable<UserRightsInfoVO> {
    return this.wagoApiClient.get(`user/rights/username/${encodeURIComponent(username)}/`, null,
      { isErrorHandlingRequired: false }).pipe(
      map((response) => this.modelConversionService.convertUserRightsInfoVO(response)));
  }

  public updateUserRightsAndRole(userRightsInfo: UserRightsInfoVO): Observable<void> {
    return this.wagoApiClient.post('user/rights/update', userRightsInfo);
  }

  public getAdditionalUserInformation(userName: string): Observable<UserVO> {
    return this.wagoApiClient.get(`user/additional-information?userName=${userName}`).pipe(
      map((response => this.modelConversionService.convertUserVO(response)))
    );
  }

  public userIsCdcUser(): Observable<boolean> {
    return this.wagoApiClient.get<boolean>('user/is-cdc-user', undefined, {
      isErrorHandlingRequired: false,
      bypassActiveCheckAndSessionValidation: true,
    });
  }

  public getAllMdmGroups(): Observable<MdmGroupVO[]> {
    return this.wagoApiClient.get<MdmGroupVO[]>('user/mdm-groups')
      .pipe(map((response) => this.modelConversionService.convertMdmGroupVOs(response)));
  }
}
